import { Link } from "react-router-dom";
import {
  Truck,
  ShieldCheck,
  Package,
  CircleCheck,
  Recycle,
  Sparkles,
  Calendar,
  Layers,
  ArrowRight,
} from "lucide-react";
import BigDemenageurCTA from "@/components/BigDemenageurCTA";

const Sections = ({ city }) => (
  <>
    {/* ======= SECTION 2 — Livraison à domicile (3 engagements) ======= */}
    <section className="py-14 md:py-20 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Livraison à domicile
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Notre service de livraison de cartons à domicile {city.aCity}
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Parce que votre temps est précieux, notre équipe logistique prend
            en charge l&apos;acheminement complet de vos packs de cartons
            directement chez vous grâce à trois engagements stricts.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-3 gap-4 md:gap-5">
          {[
            {
              title: "Une proximité immédiate",
              body: "Nous livrons à plat et au sec l'intégralité de vos commandes partout au Chesnay-Rocquencourt, ainsi que dans les communes voisines comme Versailles, Bailly, La Celle-Saint-Cloud, Louveciennes et Bougival.",
              Icon: Truck,
            },
            {
              title: "Une ponctualité rigoureuse",
              body: "Nous fixons un créneau horaire précis et adapté à votre emploi du temps pour vous éviter les longues heures d'attente inutiles et vous permettre de cartonner sans attendre.",
              Icon: Calendar,
            },
            {
              title: "Un stock toujours disponible",
              body: "Du kit de démarrage pour appartement compact aux volumes industriels pour grandes propriétés familiales, nous répondons instantanément à toutes vos demandes de réapprovisionnement.",
              Icon: Layers,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="bg-white rounded-2xl border border-[#cfdbe6] p-5 md:p-6"
            >
              <div className="flex items-center gap-2.5">
                <span className="inline-flex items-center justify-center h-10 w-10 rounded-full bg-[#237cb5] text-white">
                  <Icon className="h-5 w-5" strokeWidth={2.4} />
                </span>
                <h3 className="font-display font-extrabold text-[15px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
              </div>
              <p className="mt-3 text-[14px] text-[#3a4856] leading-relaxed">
                {body}
              </p>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= SECTION 3 — Cartons renforcés pour objets lourds ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="max-w-3xl">
          <div className="overline" style={{ color: "#237cb5" }}>
            Cartons renforcés
          </div>
          <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
            Cartons renforcés pour objets lourds : la sécurité avant tout
          </h2>
          <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
            Certaines pièces de votre mobilier ou de vos collections
            personnelles demandent une résistance structurelle bien supérieure
            aux standards du marché.
          </p>
        </div>

        <div className="mt-8 grid grid-cols-1 md:grid-cols-2 gap-4 md:gap-5">
          {[
            {
              title: "Double cannelure de qualité industrielle pour archives et livres",
              body: "Nos cartons petits formats pour charges lourdes sont dotés de parois doublées qui empêchent l'écrasement ou la déformation lors de l'empilage. Ils sont spécialement calibrés pour accueillir vos encyclopédies, documents administratifs denses ou outils, garantissant que le fond ne cédera jamais sous le poids lors de la manutention.",
              Icon: ShieldCheck,
            },
            {
              title: "Poignées latérales renforcées pour protéger les manutentionnaires",
              body: "Pour sécuriser les manipulations dans les escaliers ou les ascenseurs du Chesnay-Rocquencourt, ces cartons spécifiques intègrent des fentes de portage indéchirables. Cela assure une prise en main ferme et ergonomique, limitant l'effort et réduisant drastiquement le risque de chute de vos objets les plus denses.",
              Icon: Package,
            },
          ].map(({ title, body, Icon }) => (
            <div
              key={title}
              className="flex items-start gap-4 bg-[#EEF3F7] border border-[#cfdbe6] rounded-2xl p-5 md:p-6"
            >
              <span className="flex items-center justify-center shrink-0 h-11 w-11 rounded-full bg-[#237cb5] text-white">
                <Icon className="h-5 w-5" strokeWidth={2.4} />
              </span>
              <div>
                <h3 className="font-display font-extrabold text-[15.5px] text-[#0f1d2b] leading-tight">
                  {title}
                </h3>
                <p className="mt-2 text-[14px] text-[#3a4856] leading-relaxed">
                  {body}
                </p>
              </div>
            </div>
          ))}
        </div>
      </div>
    </section>

    {/* ======= CTA Big Déménagement (au milieu) ======= */}
    <section className="py-12 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <BigDemenageurCTA testId={`city-${city.slug}-bigdem-cta-middle`} />
      </div>
    </section>

    {/* ======= SECTION 4 — Cartons 100 % recyclables / Éco ======= */}
    <section className="py-14 md:py-20 bg-white">
      <div className="container-cdm">
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 items-start">
          <div className="lg:col-span-7">
            <div className="overline" style={{ color: "#237cb5" }}>
              Éco-responsable
            </div>
            <h2 className="mt-3 font-display font-extrabold text-[26px] sm:text-[30px] md:text-[36px] text-[#0f1d2b]">
              Des cartons 100 % recyclables pour un déménagement
              éco-responsable
            </h2>
            <p className="mt-5 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Engagés dans la préservation de l&apos;environnement, nous
              proposons exclusivement des cartons de déménagement 100 %
              recyclables et biodégradables, fabriqués en France à partir de
              fibres de cellulose biosourcées et issues de forêts gérées
              durablement.
            </p>
            <p className="mt-4 text-[15px] md:text-base text-[#3a4856] leading-relaxed">
              Choisir notre boutique en ligne pour vos fournitures
              {" "}{city.aCity}, c&apos;est opter pour un achat éco-responsable
              qui réduit l&apos;empreinte carbone de votre transition
              écologique. Grâce à une haute résistance à la compression
              verticale, chaque emballage en carton kraft brun est conçu pour
              être réutilisé sur plusieurs cycles de transport ou reconverti
              en bac de stockage, alliant ainsi démarche zéro déchet,
              performance logistique pro et économie circulaire au meilleur
              prix du web.
            </p>

            <Link
              to="/cartons-demenagement"
              className="mt-7 inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-[#237cb5] hover:bg-[#1f6da0] text-white font-bold text-[14px] transition-colors"
              data-testid="city-le-chesnay-cta-shop"
            >
              <Package className="h-4 w-4" strokeWidth={2.6} />
              Voir tous les cartons éco-responsables
              <ArrowRight className="h-4 w-4" strokeWidth={2.6} />
            </Link>
          </div>

          <div className="lg:col-span-5 bg-[#EEF3F7] border border-[#cfdbe6] rounded-3xl p-6">
            <div className="flex items-center gap-3">
              <span className="flex items-center justify-center h-12 w-12 rounded-full bg-[#237cb5] text-white">
                <Recycle className="h-6 w-6" strokeWidth={2.4} />
              </span>
              <div className="font-display font-extrabold text-[20px] text-[#0f1d2b] leading-tight">
                100 % recyclable
              </div>
            </div>
            <p className="mt-4 text-[14px] text-[#3a4856] leading-relaxed">
              Fabrication française, fibres biosourcées, forêts gérées
              durablement et structure réutilisable plusieurs cycles : un
              choix concret pour votre déménagement {city.aCity}.
            </p>
            <div className="mt-5 space-y-2.5 text-[13px] text-[#3a4856]">
              <div className="flex items-center gap-2">
                <CircleCheck className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                100 % recyclable & biodégradable
              </div>
              <div className="flex items-center gap-2">
                <CircleCheck className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Fabriqué en France
              </div>
              <div className="flex items-center gap-2">
                <CircleCheck className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Réutilisable sur plusieurs cycles
              </div>
              <div className="flex items-center gap-2">
                <Sparkles className="h-4 w-4 text-[#237cb5]" strokeWidth={2.6} />
                Démarche zéro déchet
              </div>
            </div>
          </div>
        </div>
      </div>
    </section>

    {/* ======= SECTION 5 — CTA retour département ======= */}
    <section className="py-10 md:py-14 bg-[#EEF3F7] border-y border-[#cfdbe6]">
      <div className="container-cdm">
        <Link
          to={`/cartons-demenagement/${city.deptSlug}`}
          className="inline-flex items-center gap-2 h-11 px-5 rounded-xl bg-white border border-[#cfdbe6] hover:border-[#237cb5] hover:text-[#237cb5] text-[#0f1d2b] font-bold text-[14px] transition-colors"
          data-testid="city-le-chesnay-cta-back-dept"
        >
          <Package className="h-4 w-4" strokeWidth={2.6} />
          Voir la page {city.deptName} ({city.deptCode})
        </Link>
      </div>
    </section>
  </>
);

export default { Sections };
